/**
 * Sort utility functions for ReportSort component
 */

import {
  ColumnDataType,
  SortDirection,
  SortableColumn,
  SortOption,
  EditModeState,
  ViewModeDisplay,
} from '@/types/sort';

/**
 * Get default sort direction based on column data type
 * Numeric columns default to DESC, text columns default to ASC
 */
export const getDefaultDirection = (dataType: ColumnDataType): SortDirection => {
  const numericTypes: ColumnDataType[] = ['number', 'currency', 'date', 'datetime'];
  return numericTypes.includes(dataType) ? 'desc' : 'asc';
};

/**
 * Debounce function to prevent excessive API calls
 * Used for reportQuery updates when sort changes rapidly
 */
export const debounce = <T extends (...args: any[]) => any>(
  func: T,
  wait: number,
): ((...args: Parameters<T>) => void) => {
  let timeout: ReturnType<typeof setTimeout>;

  return (...args: Parameters<T>) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
};

/**
 * Convert sortBy and sortDesc arrays to SortOption objects
 * Used for initializing component state from pageOptions
 */
export const convertArraysToSortOptions = (
  sortBy: string[],
  sortDesc: boolean[],
  sortableColumns: SortableColumn[],
): { primary: any; secondary: any } => {
  // Handle undefined/null arrays
  const safeSortBy = sortBy || [];
  const safeSortDesc = sortDesc || [];
  const safeColumns = sortableColumns || [];

  const primary = safeSortBy.length > 0 ? {
    column: safeSortBy[0],
    direction: safeSortDesc[0] ? 'desc' : 'asc',
  } : { column: '', direction: 'asc' };

  const secondary = safeSortBy.length > 1 ? {
    column: safeSortBy[1],
    direction: safeSortDesc[1] ? 'desc' : 'asc',
  } : null;

  // Validate that columns exist in sortableColumns
  if (primary.column && safeColumns.length > 0) {
    const primaryExists = safeColumns.some((col) => col.code === primary.column);
    if (!primaryExists) {
      primary.column = '';
    }
  }

  if (secondary && secondary.column && safeColumns.length > 0) {
    const secondaryExists = safeColumns.some((col) => col.code === secondary.column);
    if (!secondaryExists) {
      return { primary, secondary: null };
    }
  }

  return { primary, secondary };
};

/**
 * Convert SortOption objects to sortBy and sortDesc arrays
 * Used for emitting sort changes to parent component
 */
export const convertSortOptionsToArrays = (
  primarySort: { column: string; direction: string },
  secondarySort: { column: string; direction: string } | null,
): { sortBy: string[]; sortDesc: boolean[] } => {
  const sortBy: string[] = [];
  const sortDesc: boolean[] = [];

  if (primarySort.column) {
    sortBy.push(primarySort.column);
    sortDesc.push(primarySort.direction === 'desc');
  }

  if (secondarySort && secondarySort.column) {
    sortBy.push(secondarySort.column);
    sortDesc.push(secondarySort.direction === 'desc');
  }
  return { sortBy, sortDesc };
};

/**
 * Filter available secondary columns (exclude primary selection)
 */
export const getAvailableSecondaryColumns = (
  sortableColumns: SortableColumn[],
  primaryColumn: string,
): SortableColumn[] => {
  return sortableColumns.filter((column) => column.code !== primaryColumn);
};

/**
 * Validate sort configuration
 * Ensures sort state is consistent and valid
 */
export const validateSortState = (
  primarySort: { column: string; direction: string },
  secondarySort: { column: string; direction: string } | null,
  sortableColumns: SortableColumn[],
): boolean => {
  // Check if primary column exists in sortable columns
  if (primarySort.column) {
    const primaryExists = sortableColumns.some((col) => col.code === primarySort.column);
    if (!primaryExists) { return false; }
  }

  // Check if secondary column exists and is different from primary
  if (secondarySort && secondarySort.column) {
    const secondaryExists = sortableColumns.some((col) => col.code === secondarySort.column);
    if (!secondaryExists) { return false; }

    if (secondarySort.column === primarySort.column) { return false; }
  }

  return true;
};

/**
 * Get column display name by code
 */
export const getColumnDisplayName = (
  columnCode: string,
  sortableColumns: SortableColumn[],
): string => {
  if (!sortableColumns || sortableColumns.length === 0) {
    return cleanColumnName(columnCode);
  }

  const column = sortableColumns.find((col) => col.code === columnCode);

  if (!column) {
    return cleanColumnName(columnCode);
  }

  // Use column.name if it exists and is not empty, otherwise fall back to columnCode
  const displayName = (column.name && column.name.trim()) ? column.name : columnCode;
  return cleanColumnName(displayName);
};

/**
 * Clean column name by removing special characters
 */
export const cleanColumnName = (name: string): string => {
  if (!name) { return ''; }
  return name;
//   // More aggressive cleaning to ensure brackets are removed
//   let cleaned = name
//     // First pass: remove all types of brackets and parentheses
//     .replace(/[\[\](){}]/g, '')
//     // Second pass: handle any remaining bracket-like characters
//     .replace(/[⟨⟩⟪⟫⟬⟭⟮⟯]/g, '')
//     // Replace underscores and hyphens with spaces
//     .replace(/[_-]/g, ' ')
//     // Replace multiple spaces with single space
//     .replace(/\s+/g, ' ')
//     // Remove leading/trailing spaces
//     .trim();

//   return cleaned;
};

/**
 * Check if current sort state has any active sorting
 */
export const hasActiveSorting = (
  primarySort: { column: string; direction: string },
  secondarySort: { column: string; direction: string } | null,
): boolean => {
  return !!(primarySort.column || (secondarySort && secondarySort.column));
};

/**
 * Reset sort state to empty/default values
 */
export const getEmptySortState = () => ({
  primarySort: { column: '', direction: 'asc' as SortDirection },
  secondarySort: null,
  showSecondarySort: false,
});

/**
 * NEW: Generate compact sort summary text for view mode
 * Creates text like "Vendor ↑ Orders Count ↓"
 */
export const generateSortSummaryText = (
  sorts: SortOption[],
  getColumnName: (code: string) => string,
): string => {
  if (!sorts || sorts.length === 0) {
    return 'No sorting';
  }

  return sorts
    .filter((sort) => sort && sort.column) // Only include sorts with valid columns
    .map((sort) => {
      try {
        const columnName = getColumnName(sort.column);
        return `${columnName} ${sort.direction === 'asc' ? '↑' : '↓'}`;
      } catch (error) {
        // Fallback to column code if getColumnName fails
        return `${sort.column} ${sort.direction === 'asc' ? '↑' : '↓'}`;
      }
    })
    .join(' '); // Changed from ', ' to ' ' to remove commas
};

/**
 * NEW: Create edit state from current sort options
 * Converts current sort state to edit mode state structure
 */
export const createEditStateFromSorts = (
  primarySort: SortOption,
  secondarySort: SortOption | null,
  sortableColumns: SortableColumn[],
): EditModeState => {
  return {
    primarySort,
    secondarySort,
    showSecondarySort: !!secondarySort,
    availableSecondaryColumns: getAvailableSecondaryColumns(sortableColumns, primarySort.column),
  };
};

/**
 * NEW: Create view mode display data
 * Generates data needed for compact view mode display
 */
export const createViewModeDisplay = (
  primarySort: SortOption,
  secondarySort: SortOption | null,
  getColumnName: (code: string) => string,
): ViewModeDisplay => {
  const activeSorts: SortOption[] = [];

  if (primarySort && primarySort.column) {
    activeSorts.push(primarySort);
  }

  if (secondarySort && secondarySort.column) {
    activeSorts.push(secondarySort);
  }

  return {
    sortSummaryText: generateSortSummaryText(activeSorts, getColumnName),
    activeSorts,
    hasAnySorting: activeSorts.length > 0,
  };
};

/**
 * NEW: Toggle sort direction for quick direction changes in view mode
 */
export const toggleSortDirection = (currentDirection: SortDirection): SortDirection => {
  return currentDirection === 'asc' ? 'desc' : 'asc';
};

/**
 * NEW: Create temporary sort state for edit mode
 */
export const createTempSortState = (
  primarySort: SortOption,
  secondarySort: SortOption | null,
) => ({
  primarySort: { ...primarySort },
  secondarySort: secondarySort ? { ...secondarySort } : null,
  showSecondarySort: !!secondarySort,
});

/**
 * Log reportQuery size for performance monitoring
 * Helps track compression efficiency
 */
export const logReportQuerySize = (reportQuery: string, context: string) => {
  if (process.env.NODE_ENV === 'development') {
    const sizeKB = new Blob([reportQuery]).size / 1024;
    if (sizeKB > 2) { // URL length limit consideration
      // Performance monitoring - size warning
    } else {
      // Performance monitoring - size ok
    }
  }
};
